import React, { useState, useEffect, useRef } from 'react';
import Modal from './Modal';
import { fileService } from '../services/api';
import { toast } from 'react-toastify';
import { FaCopy, FaCheck, FaLock, FaUnlock, FaPlus, FaTrash } from 'react-icons/fa';
import LazyImage from './LazyImage';
import FileIcon from './FileIcon';
import { getFileType } from '../utils/fileUtils';

const ShareFileModal = ({ isOpen, onClose, files, onShareComplete, existingFolder = null, onFilesUpdated = null }) => {
  const [password, setPassword] = useState('');
  const [hasPassword, setHasPassword] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [folderDetails, setFolderDetails] = useState(null);
  const [addingFiles, setAddingFiles] = useState(false);
  const [removingFiles, setRemovingFiles] = useState(false);
  const [currentTempFolder, setCurrentTempFolder] = useState(null);
  const [folderName, setFolderName] = useState('');
  const [filesToShare, setFilesToShare] = useState([]); // Estado local para manejar archivos antes de crear link
  const [shareType, setShareType] = useState(null); // Tipo de compartido: 'zip' o 'html'
  const [uploadProgress, setUploadProgress] = useState(0); // Progreso estimado de la operación
  const progressPollRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Si hay una carpeta existente, cargar sus detalles (no resetear)
      if (existingFolder) {
        setShareLink(existingFolder.publicUrl);
        loadFolderDetails(existingFolder.token);
        setCurrentTempFolder(existingFolder);
        return; // No resetear si es una carpeta existente
      }
      
      // Solo resetear si no hay shareLink existente (para permitir ver el resultado después de crear)
      if (!shareLink) {
        setPassword('');
        setHasPassword(false);
        setSharing(false);
        setCopied(false);
        setFolderDetails(null);
        setAddingFiles(false);
        setRemovingFiles(false);
        setCurrentTempFolder(null);
        setFolderName('');
        setShareType(null);
        setUploadProgress(0);
      }
      
      // Inicializar archivos a compartir desde props
      if (files && files.length > 0) {
        const fileKeys = files.map(f => {
          if (typeof f === 'string') return f;
          return f.Key || f.name || f;
        }).filter(key => key && typeof key === 'string' && !key.endsWith('/'));
        setFilesToShare(fileKeys);
      } else {
        setFilesToShare([]);
      }
    } else {
      // Cuando se cierra el modal, resetear todo
      if (progressPollRef.current) {
        clearInterval(progressPollRef.current);
        progressPollRef.current = null;
      }
      setShareLink(null);
      setShareType(null);
      setPassword('');
      setHasPassword(false);
      setCopied(false);
      setUploadProgress(0);
    }
  }, [isOpen, existingFolder, files]);

  const startProgressPolling = (token) => {
    if (progressPollRef.current) {
      clearInterval(progressPollRef.current);
    }
    progressPollRef.current = setInterval(async () => {
      try {
        const details = await fileService.getSharedLinkDetails(token);
        if (details?.progress?.total > 0) {
          const pct = Math.min(100, Math.round((details.progress.copied / details.progress.total) * 100));
          setUploadProgress(pct);
        }
        if (details?.status === 'ready') {
          setUploadProgress(100);
          clearInterval(progressPollRef.current);
          progressPollRef.current = null;
        } else if (details?.status === 'failed') {
          clearInterval(progressPollRef.current);
          progressPollRef.current = null;
          toast.error(details.error || 'Error al copiar archivos', { autoClose: 4000 });
        }
      } catch (err) {
        // Si falla el polling, no romper el flujo; reintentar
      }
    }, 2000);
  };

  const loadFolderDetails = async (token) => {
    try {
      const result = await fileService.getSharedLinkDetails(token);
      if (result.success) {
        setFolderDetails(result);
      }
    } catch (error) {
      console.error('Error cargando detalles de carpeta temporal:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Si es una carpeta existente, agregar archivos
    const activeFolder = existingFolder || currentTempFolder;
    if (activeFolder && folderDetails) {
      if (!files || files.length === 0) {
        toast.error('No hay archivos para agregar', { autoClose: 2000 });
        return;
      }

      setAddingFiles(true);
      setUploadProgress(0);
      let progressIntervalId = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 1.5, 90));
      }, 100);
      
      try {
        const fileKeys = files.map(f => f.Key || f.name || f);
        const result = await fileService.addFilesToSharedLink(activeFolder.token, fileKeys);
        
        if (result.success) {
          setUploadProgress(100);
          toast.success(result.message || 'Archivos agregados exitosamente', { autoClose: 3000 });
          await loadFolderDetails(activeFolder.token);
          if (onShareComplete) {
            onShareComplete(result);
          }
        } else {
          toast.error(result.message || 'Error al agregar archivos', { autoClose: 3000 });
        }
      } catch (error) {
        console.error('Error agregando archivos:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Error desconocido';
        toast.error('Error al agregar archivos: ' + errorMessage, { autoClose: 3000 });
      } finally {
        if (progressIntervalId) {
          clearInterval(progressIntervalId);
        }
        setUploadProgress(0);
        setAddingFiles(false);
      }
      return;
    }
    
    // Crear nueva carpeta temporal (puede estar vacía)
    setSharing(true);
    setUploadProgress(0);
    
    // Usar archivos de la lista local (filesToShare) en lugar de props
    const fileKeys = filesToShare.filter(key => {
      // Filtrar carpetas y keys vacías
      if (!key || typeof key !== 'string') return false;
      if (key.endsWith('/')) {
        console.warn(`⚠️ Filtrando carpeta: ${key}`);
        return false;
      }
      return true;
    });
    
    // Si no hay archivos, mostrar advertencia
    if (fileKeys.length === 0) {
      setUploadProgress(0);
      toast.warning(
        'No hay archivos para compartir. Agrega archivos a la carpeta temporal primero.',
        { autoClose: 4000 }
      );
      setSharing(false);
      return;
    }
    
    // Simular progreso estimado basado en cantidad de archivos
    const fileCount = fileKeys.length;
    const isLargeBatch = fileCount > 20;
    const estimatedTime = isLargeBatch ? Math.min(fileCount * 0.5, 300) : Math.min(fileCount * 0.2, 30); // Segundos estimados
    const progressInterval = 100; // Actualizar cada 100ms
    const progressIncrement = (100 / (estimatedTime * 1000)) * progressInterval; // Incremento por intervalo
    
    // Iniciar simulación de progreso
    let progressIntervalId = null;
    
    try {
      progressIntervalId = setInterval(() => {
        setUploadProgress(prev => {
          const newProgress = prev + progressIncrement;
          // No pasar del 90% hasta que termine realmente
          return Math.min(newProgress, 90);
        });
      }, progressInterval);
      
      // Determinar nombre por defecto si no se proporciona
      const defaultName = files && files.length === 1 
        ? (folderName.trim() || '1 archivo')
        : (folderName.trim() || 'Carpeta compartida');
      
      const result = await fileService.shareFiles(fileKeys, hasPassword ? password : null, defaultName);
      
      // Completar progreso al 100%
      if (progressIntervalId) {
        clearInterval(progressIntervalId);
      }
      setUploadProgress(100);
      
      if (result.success) {
        console.log('📋 Resultado completo del backend:', result);
        console.log('📋 publicUrl recibido:', result.publicUrl);
        setShareLink(result.publicUrl);
        
        // Verificar si hay shareType en la respuesta
        if (result.shareType) {
          setShareType(result.shareType);
          console.log('📋 shareType recibido del backend:', result.shareType);
        }
        
        const successMessage = fileKeys.length > 0 
          ? 'Link de compartir creado exitosamente' 
          : 'Carpeta temporal vacía creada. Selecciona archivos para agregarlos.';
        
        toast.success(successMessage, { autoClose: 3000 });

        if (result.status === 'copying' && result.token) {
          setUploadProgress(0);
          startProgressPolling(result.token);
        } else {
          setUploadProgress(100);
        }
        
        // Limpiar tempFolderFiles después de compartir exitosamente
        if (onFilesUpdated) {
          onFilesUpdated([]);
        }
        
        // Si se creó una carpeta vacía, cargar sus detalles para gestionarla
        if (fileKeys.length === 0 && result.token) {
          // Esperar un momento para que el backend guarde la carpeta
          setTimeout(async () => {
            try {
              const details = await fileService.getSharedLinkDetails(result.token);
              if (details.success) {
                setFolderDetails(details);
                setCurrentTempFolder({
                  token: result.token,
                  publicUrl: result.publicUrl
                });
              }
            } catch (err) {
              console.error('Error cargando detalles de carpeta recién creada:', err);
            }
          }, 500);
        }
        
        if (onShareComplete) {
          onShareComplete(result);
        }
      } else {
        toast.error(result.message || 'Error al crear link de compartir', { autoClose: 3000 });
      }
    } catch (error) {
      if (progressIntervalId) {
        clearInterval(progressIntervalId);
      }
      setUploadProgress(0);
      console.error('Error compartiendo archivos:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error desconocido';
      toast.error('Error al compartir archivos: ' + errorMessage, { autoClose: 3000 });
    } finally {
      setSharing(false);
      // Limpiar intervalo si aún existe
      if (progressIntervalId) {
        clearInterval(progressIntervalId);
      }
    }
  };

  // Eliminar archivo de la lista antes de crear el link
  const handleRemoveFileFromList = (fileKey) => {
    setFilesToShare(prev => {
      const updated = prev.filter(key => key !== fileKey);
      // Notificar al padre para actualizar tempFolderFiles
      if (onFilesUpdated) {
        onFilesUpdated(updated);
      }
      return updated;
    });
    toast.info('Archivo eliminado de la lista', { autoClose: 2000 });
  };

  // Eliminar todos los archivos de la lista
  const handleRemoveAllFiles = () => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar todos los archivos de la lista?')) {
      return;
    }
    setFilesToShare([]);
    // Notificar al padre para actualizar tempFolderFiles
    if (onFilesUpdated) {
      onFilesUpdated([]);
    }
    toast.info('Todos los archivos eliminados de la lista', { autoClose: 2000 });
  };

  // Eliminar archivo de carpeta temporal existente
  const handleRemoveFile = async (fileKey) => {
    if (!window.confirm('¿Estás seguro de que quieres quitar este archivo de la carpeta temporal?')) {
      return;
    }

    const activeFolder = existingFolder || currentTempFolder;
    if (!activeFolder) return;

    setRemovingFiles(true);
    
    try {
      const result = await fileService.removeFilesFromSharedLink(activeFolder.token, [fileKey]);
      
      if (result.success) {
        toast.success(result.message || 'Archivo eliminado exitosamente', { autoClose: 3000 });
        await loadFolderDetails(activeFolder.token);
      } else {
        toast.error(result.message || 'Error al quitar archivo', { autoClose: 3000 });
      }
    } catch (error) {
      console.error('Error quitando archivo:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error desconocido';
      toast.error('Error al quitar archivo: ' + errorMessage, { autoClose: 3000 });
    } finally {
      setRemovingFiles(false);
    }
  };

  const handleCopyLink = () => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success('Link copiado al portapapeles', { autoClose: 2000 });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setShareLink(null);
    setPassword('');
    setHasPassword(false);
    onClose();
  };

  const isManagingExisting = (existingFolder || currentTempFolder) && folderDetails;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={isManagingExisting ? "Gestionar carpeta temporal" : "Compartir archivos temporales"} size="lg">
      {isManagingExisting ? (
        // Vista de gestión de carpeta temporal existente
        <div className="space-y-6">
          {/* Link de compartir */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Link de compartir:
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={shareLink}
                readOnly
                className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
              />
              <button
                onClick={handleCopyLink}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                title="Copiar link"
              >
                {copied ? (
                  <>
                    <FaCheck />
                    <span>Copiado</span>
                  </>
                ) : (
                  <>
                    <FaCopy />
                    <span>Copiar</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Archivos existentes en la carpeta */}
          {folderDetails && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Archivos en la carpeta ({folderDetails.fileCount})
              </label>
              <div className="bg-gray-700 rounded-lg p-4 max-h-64 overflow-y-auto">
                {folderDetails.files && folderDetails.files.length > 0 ? (
                  <ul className="space-y-3">
                    {folderDetails.files.map((fileKey, idx) => {
                      const fileName = fileKey.split('/').pop();
                      const fileType = getFileType(fileName);
                      const isImageFile = fileType === 'image';
                      // fileKey ya contiene la ruta completa desde folderDetails
                      const fullFileKey = fileKey;
                      
                      return (
                        <li key={idx} className="flex items-center gap-3 text-sm text-gray-300 hover:bg-gray-600 rounded-lg p-2 transition-colors">
                          {/* Miniatura o icono */}
                          <div className="flex-shrink-0 w-16 h-16 rounded overflow-hidden bg-gray-800 border border-gray-600 flex items-center justify-center">
                            {isImageFile ? (
                              <LazyImage
                                fileName={fullFileKey}
                                alt={fileName}
                                className="w-full h-full"
                                width={64}
                                height={64}
                                quality={60}
                                fallbackIcon={<FileIcon fileName={fileName} size="text-2xl" />}
                              />
                            ) : (
                              <FileIcon fileName={fileName} size="text-2xl" />
                            )}
                          </div>
                          
                          {/* Nombre del archivo */}
                          <span className="truncate flex-1 min-w-0">
                            {fileName}
                          </span>
                          
                          {/* Botón eliminar */}
                          <button
                            onClick={() => handleRemoveFile(fileName)}
                            disabled={removingFiles}
                            className="ml-2 p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded transition-colors disabled:opacity-50 flex-shrink-0"
                            title="Quitar archivo"
                          >
                            <FaTrash className="text-sm" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-400">No hay archivos en la carpeta</p>
                )}
              </div>
            </div>
          )}

          {/* Agregar nuevos archivos */}
          {files && files.length > 0 && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Archivos a agregar ({files.length})
                </label>
                <div className="bg-gray-700 rounded-lg p-4 max-h-48 overflow-y-auto">
                  <ul className="space-y-3">
                    {files.map((file, idx) => {
                      const fileName = file.Key || file.name || file;
                      const fileType = getFileType(fileName);
                      const isImageFile = fileType === 'image';
                      const fileKey = typeof file === 'string' ? file : (file.Key || file.name || file);
                      
                      return (
                        <li key={idx} className="flex items-center gap-3 text-sm text-gray-300 hover:bg-gray-600 rounded-lg p-2 transition-colors">
                          {/* Miniatura o icono */}
                          <div className="flex-shrink-0 w-12 h-12 rounded overflow-hidden bg-gray-800 border border-gray-600 flex items-center justify-center">
                            {isImageFile ? (
                              <LazyImage
                                fileName={fileKey}
                                alt={fileName}
                                className="w-full h-full"
                                width={48}
                                height={48}
                                quality={60}
                                fallbackIcon={<FileIcon fileName={fileName} size="text-xl" />}
                              />
                            ) : (
                              <FileIcon fileName={fileName} size="text-xl" />
                            )}
                          </div>
                          
                          {/* Nombre del archivo */}
                          <span className="truncate flex-1 min-w-0 flex items-center gap-2">
                            <FaPlus className="text-green-400 flex-shrink-0" />
                            {fileName}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Cerrar
                </button>
                <button
                  type="submit"
                  disabled={addingFiles || !files || files.length === 0}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {addingFiles ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Agregando...</span>
                    </>
                  ) : (
                    <>
                      <FaPlus />
                      <span>Agregar archivos</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      ) : !shareLink ? (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Lista de archivos a compartir */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-300">
                Archivos a compartir ({filesToShare.length})
              </label>
              {filesToShare.length > 0 && (
                <button
                  type="button"
                  onClick={handleRemoveAllFiles}
                  className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                >
                  <FaTrash className="text-xs" />
                  Eliminar todos
                </button>
              )}
            </div>
            <div className="bg-gray-700 rounded-lg p-4 max-h-64 overflow-y-auto">
              {filesToShare && filesToShare.length > 0 ? (
                <ul className="space-y-3">
                  {filesToShare.map((fileKey, idx) => {
                    const fileName = fileKey.split('/').pop();
                    const fileType = getFileType(fileName);
                    const isImageFile = fileType === 'image';
                    
                    return (
                      <li key={idx} className="flex items-center gap-3 text-sm text-gray-300 hover:bg-gray-600 rounded-lg p-2 transition-colors">
                        {/* Miniatura o icono */}
                        <div className="flex-shrink-0 w-16 h-16 rounded overflow-hidden bg-gray-800 border border-gray-600 flex items-center justify-center">
                          {isImageFile ? (
                            <LazyImage
                              fileName={fileKey}
                              alt={fileName}
                              className="w-full h-full"
                              width={64}
                              height={64}
                              quality={60}
                              fallbackIcon={<FileIcon fileName={fileName} size="text-2xl" />}
                            />
                          ) : (
                            <FileIcon fileName={fileName} size="text-2xl" />
                          )}
                        </div>
                        
                        {/* Nombre del archivo */}
                        <span className="truncate flex-1 min-w-0">
                          {fileName}
                        </span>
                        
                        {/* Botón eliminar */}
                        <button
                          type="button"
                          onClick={() => handleRemoveFileFromList(fileKey)}
                          className="ml-2 p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded transition-colors flex-shrink-0"
                          title="Eliminar archivo"
                        >
                          <FaTrash className="text-sm" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-gray-400">No hay archivos seleccionados</p>
              )}
            </div>
          </div>

          {/* Nombre/Título del material compartido */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {filesToShare.length === 1 ? 'Título del archivo compartido' : 'Nombre del material compartido'}
            </label>
            <input
              type="text"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder={files && files.length === 1 ? 'Ej: Foto del evento' : 'Ej: Evento Enero 2026'}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
            />
            <p className="mt-2 text-xs text-gray-400">
              {filesToShare.length === 1 
                ? 'Si no ingresas un título, se mostrará "1 archivo"' 
                : 'Si no ingresas un nombre, se usará "Carpeta compartida"'}
            </p>
          </div>

          {/* Información de expiración */}
          <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4">
            <p className="text-sm text-blue-200">
              <strong>ℹ️ Información:</strong> El link expirará automáticamente en 5 días. 
              Los archivos se eliminarán después de compartir.
            </p>
          </div>

          {/* Contraseña opcional */}
          <div>
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={hasPassword}
                onChange={(e) => {
                  setHasPassword(e.target.checked);
                  if (!e.target.checked) setPassword('');
                }}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-300 flex items-center">
                {hasPassword ? <FaLock className="mr-2" /> : <FaUnlock className="mr-2" />}
                Proteger con contraseña
              </span>
            </label>
            
            {hasPassword && (
              <div className="mt-3">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Ingresa una contraseña (mínimo 4 caracteres)"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                  required={hasPassword}
                  minLength={4}
                />
                <p className="mt-2 text-xs text-gray-400">
                  La contraseña debe tener al menos 4 caracteres
                </p>
              </div>
            )}
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={sharing || filesToShare.length === 0 || (hasPassword && password.length < 4)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {sharing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Compartiendo...</span>
                </>
              ) : (
                <span>{files && files.length > 0 ? 'Crear link de compartir' : 'Crear carpeta temporal vacía'}</span>
              )}
            </button>
          </div>
          
          {/* Barra de progreso */}
          {(sharing || addingFiles) && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm text-gray-400">
                <span>Procesando archivos...</span>
                <span>{Math.round(uploadProgress)}%</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2.5 overflow-hidden">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              {filesToShare.length > 20 && (
                <p className="text-xs text-gray-500 text-center">
                  ⏳ Procesando archivos... puede tardar unos minutos.
                </p>
              )}
            </div>
          )}
        </form>
      ) : (
        <div className="space-y-6">
          {/* Link generado */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Link de compartir creado:
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={shareLink}
                readOnly
                className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
              />
              <button
                onClick={handleCopyLink}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                title="Copiar link"
              >
                {copied ? (
                  <>
                    <FaCheck />
                    <span>Copiado</span>
                  </>
                ) : (
                  <>
                    <FaCopy />
                    <span>Copiar</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Información adicional */}
          <div className="bg-green-900/30 border border-green-700 rounded-lg p-4">
            <p className="text-sm text-green-200">
              ✅ <strong>Link creado exitosamente</strong>
            </p>
            <p className="text-xs text-green-300 mt-2">
              Comparte este link con quien quieras. Expirará en 5 días y los archivos se eliminarán automáticamente.
            </p>
          </div>

          {/* Botón cerrar */}
          <div className="flex justify-end">
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ShareFileModal;
